// [ROLES-1-EQUIPE] - Module de gestion d'équipe pour OWNER uniquement
// [MIGRATION-MONGODB] - Migré de localStorage vers API/MongoDB
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import {
  Plus,
  MoreVertical,
  UserCog,
  KeyRound,
  UserX,
  Trash2,
  X,
  RefreshCw,
  Eye,
  EyeOff,
  Check,
  Users,
  ShoppingBag,
  BarChart3,
  UserCheck,
  Copy,
  ChevronLeft,
  ChevronRight,
  User as UserIcon,
  Mail,
  Lock,
  Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { UtensilsCrossed } from "lucide-react";
import { Card } from "../components/ui/card";
import { getFeatures } from "../lib/boutiqueType";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

// [ROLES-1-EQUIPE] Générateur de mot de passe mémorisable
const generatePassword = () => {
  const words = [
    "Robe", "Vente", "Mode", "Style", "Chic", "Luxe", "Soie", "Perle",
    "Dore", "Rose", "Bleu", "Noir", "Blanc", "Satin", "Voile", "Cuir",
    "Bijou", "Eclat", "Coton", "Lin", "Laine", "Velours", "Cachemire"
  ];
  const word1 = words[Math.floor(Math.random() * words.length)];
  const word2 = words[Math.floor(Math.random() * words.length)];
  const year = new Date().getFullYear();
  const num = Math.floor(Math.random() * 100);
  return `${word1}-${word2}-${year.toString().slice(-2)}${num.toString().padStart(2, '0')}`;
};

// [EQUIPE-PASSWORD] Vérifier la force du mot de passe
const checkPasswordStrength = (pwd) => {
  if (!pwd) return { score: 0, level: '', color: '#6B7280', criteria: { length: false, uppercase: false, number: false, special: false } };
  const criteria = {
    length: pwd.length >= 8,
    uppercase: /[A-Z]/.test(pwd),
    number: /[0-9]/.test(pwd),
    special: /[^A-Za-z0-9]/.test(pwd)
  };
  const score = Object.values(criteria).filter(Boolean).length;
  const levels = ['Très faible', 'Faible', 'Moyen', 'Bien', 'Fort'];
  const colors = ['#6B7280', '#EF4444', '#F59E0B', '#3B82F6', '#22C55E'];
  return { score, level: levels[score], color: colors[score], criteria };
};

// [EQUIPE-PASSWORD] Barre d'indicateur de force
const PasswordStrengthBar = ({ password }) => {
  const strength = checkPasswordStrength(password);
  if (!password) return null;
  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1.5">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-1 flex-1 rounded-full transition-all duration-300"
            style={{ backgroundColor: strength.score >= i ? strength.color : '#2A2A2A' }} />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium" style={{ color: strength.color }}>{strength.level}</p>
        <div className="flex gap-2 text-xs text-gray-600">
          {!strength.criteria.length && <span>8 car. min</span>}
          {!strength.criteria.uppercase && <span>Majuscule</span>}
          {!strength.criteria.number && <span>Chiffre</span>}
          {!strength.criteria.special && <span>Spécial (@#$!...)</span>}
        </div>
      </div>
    </div>
  );
};

// [ROLES-1-EQUIPE] Hash simple pour simulation
const hashPassword = (password) => {
  return btoa(password);
};

// [ROLES-1-EQUIPE] Génération UUID simple
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// [ROLES-1-EQUIPE] Formater la dernière connexion
const formatLastConnection = (timestamp) => {
  if (!timestamp) return "Jamais connecté";
  
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
  return date.toLocaleDateString('fr-FR');
};

// [ROLES-1-EQUIPE] Composant Avatar avec initiales
const MemberAvatar = ({ prenom, nom, role, accentColor }) => {
  const initials = `${prenom?.[0] || ''}${nom?.[0] || ''}`.toUpperCase();
  // Utiliser la couleur accent de la boutique avec variations
  const bgColor = role === 'vendeur' 
    ? accentColor // Couleur accent pour vendeur
    : `${accentColor}99`; // Couleur accent avec transparence pour comptable
  
  return (
    <div
      className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
      style={{ backgroundColor: bgColor }}
    >
      {initials}
    </div>
  );
};

// [ROLES-1-EQUIPE] Badge de rôle
const RoleBadge = ({ role, inactive, accentColor }) => {
  if (inactive) {
    return (
      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-400">
        Inactif
      </span>
    );
  }
  
  // Utiliser la couleur accent de la boutique
  const styles = {
    vendeur: { bg: `${accentColor}20`, color: accentColor, label: 'Vendeur' },
    comptable: { bg: `${accentColor}15`, color: `${accentColor}CC`, label: 'Comptable' },
    serveur: { bg: '#8B5CF625', color: '#A78BFA', label: 'Serveur' }
  };
  
  const style = styles[role] || styles.vendeur;
  
  return (
    <span
      className="px-3 py-1 rounded-full text-xs font-medium"
      style={{ backgroundColor: style.bg, color: style.color }}
    >
      {style.label}
    </span>
  );
};

// [ROLES-1-EQUIPE] Carte membre
const MemberCard = ({ member, onEdit, onResetPassword, onToggleActive, onDelete, accentColor }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`rounded-[14px] p-4 px-5 mb-3 bg-secondary ${!member.actif ? 'opacity-50' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <MemberAvatar prenom={member.prenom} nom={member.nom} role={member.role} accentColor={accentColor} />
          <div>
            <p className="text-foreground font-semibold text-[15px]">
              {member.prenom} {member.nom}
            </p>
            <p className="text-muted-foreground text-[13px]">{member.email}</p>
            <p className="text-muted-foreground/70 text-[12px] mt-0.5">
              Dernière connexion : {formatLastConnection(member.derniereConnexion)}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <RoleBadge role={member.role} inactive={!member.actif} accentColor={accentColor} />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="w-4 h-4 text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => onEdit(member)}>
                <UserCog className="w-4 h-4 mr-2" />
                Modifier le rôle
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onResetPassword(member)}>
                <KeyRound className="w-4 h-4 mr-2" />
                Réinitialiser mot de passe
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onToggleActive(member)}
                className={member.actif ? "text-orange-400" : "text-green-400"}
              >
                {member.actif ? (
                  <>
                    <UserX className="w-4 h-4 mr-2" />
                    Désactiver le compte
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 mr-2" />
                    Réactiver le compte
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDelete(member)}
                className="text-red-400"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </motion.div>
  );
};

// [ROLES-1-EQUIPE] État vide
const EmptyState = ({ onAdd, accentColor }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4">
    <div
      className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6"
      style={{ backgroundColor: `${accentColor}15` }}
    >
      <Users className="w-10 h-10" style={{ color: accentColor }} />
    </div>
    <h3 className="text-xl font-semibold text-white mb-2">Aucun membre pour l'instant</h3>
    <p className="text-gray-400 text-center max-w-sm mb-6">
      Ajoutez votre premier collaborateur pour commencer à déléguer la gestion de votre boutique.
    </p>
    <Button
      onClick={onAdd}
      className="rounded-xl px-6"
      style={{ backgroundColor: accentColor }}
    >
      <Plus className="w-4 h-4 mr-2" />
      Ajouter un membre
    </Button>
  </div>
);

// [ROLES-1-EQUIPE] [WIZARD] Wizard création membre — 4 étapes mobile-first
const CreateMemberModal = ({ isOpen, onClose, onSave, accentColor, existingEmails }) => {
  const [step, setStep] = useState(1);
  const TOTAL_STEPS = 4;

  const [role, setRole] = useState('vendeur');
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [passwordMode, setPasswordMode] = useState('auto');
  const [generatedPassword, setGeneratedPassword] = useState(() => generatePassword());
  const [showGenerated, setShowGenerated] = useState(true);
  const [manualPassword, setManualPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showManualPwd, setShowManualPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [copied, setCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setRole('vendeur'); setPrenom(''); setNom(''); setEmail('');
      setPasswordMode('auto'); setGeneratedPassword(generatePassword());
      setShowGenerated(true); setManualPassword(''); setConfirmPassword('');
      setShowManualPwd(false); setShowConfirmPwd(false);
      setEmailError(''); setConfirmError(''); setCopied(false);
    }
  }, [isOpen]);

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) { setEmailError('Format email invalide'); return false; }
    if (existingEmails.includes(value.toLowerCase())) { setEmailError('Cet email est déjà utilisé'); return false; }
    setEmailError(''); return true;
  };

  const strength = checkPasswordStrength(manualPassword);

  const canProceedStep = () => {
    if (step === 1) return !!role;
    if (step === 2) return prenom.trim().length > 0 && nom.trim().length > 0;
    if (step === 3) return email.trim().length > 0 && !emailError;
    if (step === 4) {
      if (passwordMode === 'auto') return !!generatedPassword;
      return manualPassword.length > 0 && strength.score >= 4 && manualPassword === confirmPassword;
    }
    return false;
  };

  const handleNext = () => {
    if (step === 3) {
      if (!validateEmail(email)) return;
    }
    if (step < TOTAL_STEPS) setStep(step + 1);
    else handleSubmit();
  };

  const handlePrev = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!prenom || !nom || !email || !role) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    if (!validateEmail(email)) { setStep(3); return; }

    if (passwordMode === 'manual') {
      if (!manualPassword) { toast.error("Entrez un mot de passe"); return; }
      if (strength.score < 4) { toast.error("Le mot de passe est trop faible. Respectez tous les critères."); return; }
      if (manualPassword !== confirmPassword) { setConfirmError("Les mots de passe ne correspondent pas"); return; }
    }

    setIsSubmitting(true);
    const finalPassword = passwordMode === 'auto' ? generatedPassword : manualPassword;

    const newMember = {
      id: generateUUID(),
      prenom: prenom.trim(),
      nom: nom.trim(),
      email: email.toLowerCase().trim(),
      plainPassword: finalPassword,
      role,
      actif: true,
      createdAt: new Date().toISOString(),
      derniereConnexion: null
    };

    await onSave(newMember);
    setIsSubmitting(false);
    onClose();
  };

  const roleOptions = [
    { id: 'vendeur', label: 'Vendeur', desc: 'Caisse · Produits · Stock', Icon: ShoppingBag },
    { id: 'comptable', label: 'Comptable', desc: 'Dashboard · Stats', Icon: BarChart3 },
    ...(getFeatures().tableOrders ? [{ id: 'serveur', label: 'Serveur', desc: 'Prise de commandes en salle', Icon: UtensilsCrossed }] : []),
  ];

  const stepMeta = {
    1: { title: 'Choisir un rôle', subtitle: 'Quel accès ce membre aura-t-il ?', Icon: UserIcon },
    2: { title: 'Identité', subtitle: 'Comment s\'appelle ce membre ?', Icon: Sparkles },
    3: { title: 'Contact', subtitle: 'Son adresse email pour se connecter', Icon: Mail },
    4: { title: 'Mot de passe', subtitle: 'Générez-le ou choisissez-le', Icon: Lock },
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center backdrop-blur-sm"
        onClick={onClose}
        data-testid="member-wizard-overlay"
      >
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 26, stiffness: 300 }}
          className="w-full sm:max-w-[520px] bg-card rounded-t-[24px] sm:rounded-[24px] max-h-[92vh] flex flex-col border border-border shadow-2xl"
          onClick={e => e.stopPropagation()}
          data-testid="member-wizard"
        >
          {/* Header + progress */}
          <div className="p-5 pb-3 border-b border-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${accentColor}18`, color: accentColor }}
                >
                  {(() => { const S = stepMeta[step].Icon; return <S className="w-4.5 h-4.5" />; })()}
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                    Étape {step} sur {TOTAL_STEPS}
                  </p>
                  <h2 className="text-foreground font-semibold text-[17px] leading-tight">
                    {stepMeta[step].title}
                  </h2>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                data-testid="wizard-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Progress bar */}
            <div className="flex gap-1.5">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className="flex-1 h-1 rounded-full transition-all duration-300"
                  style={{
                    background: s <= step ? accentColor : 'hsl(var(--secondary))',
                    opacity: s === step ? 1 : (s < step ? 0.85 : 1),
                  }}
                />
              ))}
            </div>
          </div>

          {/* Content scroll area */}
          <div className="flex-1 overflow-y-auto p-5">
            <p className="text-muted-foreground text-[13px] mb-5">
              {stepMeta[step].subtitle}
            </p>

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.18 }}
              >
                {/* Step 1: Role */}
                {step === 1 && (
                  <div className="space-y-2.5">
                    {roleOptions.map(({ id, label, desc, Icon }) => {
                      const active = role === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setRole(id)}
                          data-testid={`wizard-role-${id}`}
                          className={`w-full p-4 rounded-2xl border-2 text-left transition-all active:scale-[0.99] ${active ? '' : 'bg-secondary/50 border-border hover:border-muted-foreground/40'}`}
                          style={active ? { borderColor: accentColor, backgroundColor: `${accentColor}12` } : undefined}
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{
                                backgroundColor: active ? accentColor : 'hsl(var(--muted))',
                                color: active ? '#fff' : 'hsl(var(--muted-foreground))',
                              }}
                            >
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-foreground text-[15px]">{label}</p>
                              <p className="text-[12.5px] text-muted-foreground truncate">{desc}</p>
                            </div>
                            {active && (
                              <div
                                className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: accentColor }}
                              >
                                <Check className="w-3.5 h-3.5 text-white" />
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Step 2: Identity */}
                {step === 2 && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[13px] text-muted-foreground mb-2 font-medium">Prénom</label>
                      <input
                        type="text"
                        autoFocus
                        value={prenom}
                        onChange={(e) => setPrenom(e.target.value)}
                        placeholder="Ex : Aminata"
                        data-testid="wizard-prenom"
                        className="w-full bg-secondary border border-border rounded-2xl px-4 py-4 text-[16px] text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-foreground/40 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[13px] text-muted-foreground mb-2 font-medium">Nom</label>
                      <input
                        type="text"
                        value={nom}
                        onChange={(e) => setNom(e.target.value)}
                        placeholder="Ex : Koné"
                        data-testid="wizard-nom"
                        className="w-full bg-secondary border border-border rounded-2xl px-4 py-4 text-[16px] text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-foreground/40 transition-colors"
                      />
                    </div>
                  </div>
                )}

                {/* Step 3: Email */}
                {step === 3 && (
                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2 font-medium">Adresse email</label>
                    <input
                      type="email"
                      autoFocus
                      inputMode="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (e.target.value) validateEmail(e.target.value); else setEmailError('');
                      }}
                      placeholder="aminata@boutique.com"
                      data-testid="wizard-email"
                      className={`w-full bg-secondary border rounded-2xl px-4 py-4 text-[16px] text-foreground placeholder:text-muted-foreground/60 focus:outline-none transition-colors ${emailError ? 'border-red-500' : 'border-border focus:border-foreground/40'}`}
                    />
                    {emailError && <p className="text-red-500 text-xs mt-2">{emailError}</p>}
                    <div className="mt-4 p-3 rounded-xl bg-secondary/60 border border-border">
                      <p className="text-[12px] text-muted-foreground leading-relaxed">
                        Cette adresse servira à {prenom || 'ce membre'} pour se connecter à la boutique.
                      </p>
                    </div>
                  </div>
                )}

                {/* Step 4: Password */}
                {step === 4 && (
                  <div className="space-y-4">
                    {/* Toggle */}
                    <div className="flex bg-secondary border border-border rounded-2xl p-1">
                      <button
                        type="button"
                        onClick={() => setPasswordMode('auto')}
                        data-testid="wizard-pwd-auto"
                        className={`flex-1 py-2.5 px-3 rounded-xl text-[13px] font-medium transition-all ${passwordMode === 'auto' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
                      >
                        Générer
                      </button>
                      <button
                        type="button"
                        onClick={() => setPasswordMode('manual')}
                        data-testid="wizard-pwd-manual"
                        className={`flex-1 py-2.5 px-3 rounded-xl text-[13px] font-medium transition-all ${passwordMode === 'manual' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
                      >
                        Choisir
                      </button>
                    </div>

                    {passwordMode === 'auto' ? (
                      <div>
                        <div className="relative">
                          <input
                            type={showGenerated ? "text" : "password"}
                            value={generatedPassword}
                            readOnly
                            className="w-full bg-secondary border border-border rounded-2xl px-4 py-4 pr-28 text-foreground font-mono text-[15px]"
                            data-testid="wizard-generated-pwd"
                          />
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                            <button
                              type="button"
                              onClick={() => setGeneratedPassword(generatePassword())}
                              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                              title="Régénérer"
                              data-testid="wizard-regenerate-pwd"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowGenerated(!showGenerated)}
                              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {showGenerated ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                            <button
                              type="button"
                              onClick={handleCopy}
                              className="p-2 transition-colors"
                              title="Copier"
                              style={{ color: copied ? accentColor : undefined }}
                              data-testid="wizard-copy-pwd"
                            >
                              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
                            </button>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-[12px] mt-3 leading-relaxed">
                          Copiez ce mot de passe et communiquez-le à {prenom || 'ce membre'}. Il devra le modifier à sa première connexion.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="relative">
                          <input
                            type={showManualPwd ? "text" : "password"}
                            value={manualPassword}
                            onChange={(e) => setManualPassword(e.target.value)}
                            placeholder="Créer un mot de passe"
                            data-testid="wizard-manual-pwd"
                            className="w-full bg-secondary border border-border rounded-2xl px-4 py-4 pr-12 text-[16px] text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-foreground/40 transition-colors"
                          />
                          <button
                            type="button"
                            onClick={() => setShowManualPwd(!showManualPwd)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {showManualPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <PasswordStrengthBar password={manualPassword} />
                        <div className="relative">
                          <input
                            type={showConfirmPwd ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => { setConfirmPassword(e.target.value); if (confirmError) setConfirmError(''); }}
                            placeholder="Confirmer le mot de passe"
                            data-testid="wizard-confirm-pwd"
                            className={`w-full bg-secondary border rounded-2xl px-4 py-4 pr-12 text-[16px] text-foreground placeholder:text-muted-foreground/60 focus:outline-none transition-colors ${confirmError ? 'border-red-500' : 'border-border focus:border-foreground/40'}`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPwd(!showConfirmPwd)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {showConfirmPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {confirmError && <p className="text-red-500 text-xs">{confirmError}</p>}
                      </div>
                    )}

                    {/* Récap */}
                    <div className="mt-2 p-4 rounded-2xl bg-secondary/60 border border-border">
                      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3">Récapitulatif</p>
                      <div className="space-y-2 text-[13px]">
                        <div className="flex justify-between gap-3">
                          <span className="text-muted-foreground">Rôle</span>
                          <span className="text-foreground font-medium capitalize">{role}</span>
                        </div>
                        <div className="flex justify-between gap-3">
                          <span className="text-muted-foreground">Nom</span>
                          <span className="text-foreground font-medium truncate">{prenom} {nom}</span>
                        </div>
                        <div className="flex justify-between gap-3">
                          <span className="text-muted-foreground">Email</span>
                          <span className="text-foreground font-medium truncate max-w-[60%]">{email}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer buttons */}
          <div className="p-4 border-t border-border flex items-center gap-3">
            {step > 1 ? (
              <button
                onClick={handlePrev}
                data-testid="wizard-prev"
                className="flex items-center justify-center gap-1.5 px-4 h-12 rounded-2xl bg-secondary text-foreground font-medium text-[14px] hover:bg-muted transition-colors active:scale-[0.98]"
              >
                <ChevronLeft className="w-4 h-4" />
                Précédent
              </button>
            ) : (
              <button
                onClick={onClose}
                data-testid="wizard-cancel"
                className="flex items-center justify-center px-4 h-12 rounded-2xl bg-secondary text-foreground font-medium text-[14px] hover:bg-muted transition-colors active:scale-[0.98]"
              >
                Annuler
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={!canProceedStep() || isSubmitting}
              data-testid={step === TOTAL_STEPS ? "wizard-submit" : "wizard-next"}
              className="flex-1 flex items-center justify-center gap-1.5 h-12 rounded-2xl font-semibold text-[14px] text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
              style={{ backgroundColor: accentColor }}
            >
              {isSubmitting ? (
                "Création..."
              ) : step === TOTAL_STEPS ? (
                <>Créer le compte</>
              ) : (
                <>Suivant <ChevronRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// [ROLES-1-EQUIPE] Modale changement de rôle
const ChangeRoleModal = ({ isOpen, onClose, member, onSave, accentColor }) => {
  const [role, setRole] = useState(member?.role || 'vendeur');

  useEffect(() => {
    if (member) setRole(member.role);
  }, [member]);

  if (!isOpen || !member) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-md bg-[#1A1A1A] rounded-2xl p-6"
          onClick={e => e.stopPropagation()}
        >
          <h2 className="text-white font-bold text-lg mb-4">Modifier le rôle</h2>
          <p className="text-gray-400 text-sm mb-4">
            Changer le rôle de <span className="text-white">{member.prenom} {member.nom}</span>
          </p>

          <div className="space-y-2 mb-6">
            <button
              type="button"
              onClick={() => setRole('vendeur')}
              className={`w-full p-4 rounded-xl border text-left transition-all ${
                role === 'vendeur' ? 'border-2' : 'border-[#2A2A2A] bg-[#0F0F0F]'
              }`}
              style={role === 'vendeur' ? { borderColor: accentColor, backgroundColor: `${accentColor}10` } : {}}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ShoppingBag className="w-5 h-5" style={{ color: role === 'vendeur' ? accentColor : '#9CA3AF' }} />
                  <span className="font-semibold text-white">Vendeur</span>
                </div>
                {role === 'vendeur' && <Check className="w-5 h-5" style={{ color: accentColor }} />}
              </div>
            </button>
            <button
              type="button"
              onClick={() => setRole('comptable')}
              className={`w-full p-4 rounded-xl border text-left transition-all ${
                role === 'comptable' ? 'border-2' : 'border-[#2A2A2A] bg-[#0F0F0F]'
              }`}
              style={role === 'comptable' ? { borderColor: accentColor, backgroundColor: `${accentColor}10` } : {}}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BarChart3 className="w-5 h-5" style={{ color: role === 'comptable' ? accentColor : '#9CA3AF' }} />
                  <span className="font-semibold text-white">Comptable</span>
                </div>
                {role === 'comptable' && <Check className="w-5 h-5" style={{ color: accentColor }} />}
              </div>
            </button>
            {getFeatures().tableOrders && (
            <button
              type="button"
              onClick={() => setRole('serveur')}
              className={`w-full p-4 rounded-xl border text-left transition-all ${
                role === 'serveur' ? 'border-2' : 'border-[#2A2A2A] bg-[#0F0F0F]'
              }`}
              style={role === 'serveur' ? { borderColor: accentColor, backgroundColor: `${accentColor}10` } : {}}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <UtensilsCrossed className="w-5 h-5" style={{ color: role === 'serveur' ? accentColor : '#9CA3AF' }} />
                  <span className="font-semibold text-white">Serveur</span>
                </div>
                {role === 'serveur' && <Check className="w-5 h-5" style={{ color: accentColor }} />}
              </div>
            </button>
            )}
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
            <Button
              onClick={() => { onSave(member.id, role); onClose(); }}
              className="flex-1"
              style={{ backgroundColor: accentColor }}
            >
              Enregistrer
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// [ROLES-1-EQUIPE] Composant principal
// [MIGRATION-MONGODB] Utilise API au lieu de localStorage
export default function Team() {
  const navigate = useNavigate();
  const [team, setTeam] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [deletingMember, setDeletingMember] = useState(null);
  const [resetPasswordMember, setResetPasswordMember] = useState(null);
  const [loading, setLoading] = useState(true);

  // Données boutique
  const boutiqueData = JSON.parse(localStorage.getItem("boutique") || "{}");
  const accentColor = boutiqueData.customColor || '#D4A853';
  const token = localStorage.getItem("token");

  const apiHeaders = { Authorization: `Bearer ${token}` };

  // [MIGRATION-MONGODB] Charger l'équipe depuis l'API
  const fetchTeam = async () => {
    try {
      const res = await axios.get(`${API}/team/members`, { headers: apiHeaders });
      setTeam(res.data);
    } catch (e) {
      console.error("Error fetching team:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  // Emails existants pour validation
  const existingEmails = useMemo(() => {
    return team.map(m => m.email.toLowerCase());
  }, [team]);

  // [MIGRATION-MONGODB] Ajouter un membre via API
  const handleAddMember = async (newMember) => {
    try {
      const res = await axios.post(`${API}/team/members`, {
        prenom: newMember.prenom,
        nom: newMember.nom,
        email: newMember.email,
        password: newMember.plainPassword,
        role: newMember.role,
      }, { headers: apiHeaders });
      setTeam(prev => [...prev, res.data]);
      toast.success(`Compte ${newMember.prenom} créé avec succès !`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création");
    }
  };

  // [MIGRATION-MONGODB] Modifier le rôle via API
  const handleChangeRole = async (memberId, newRole) => {
    try {
      const res = await axios.put(`${API}/team/members/${memberId}`, { role: newRole }, { headers: apiHeaders });
      setTeam(prev => prev.map(m => m.id === memberId ? res.data : m));
      toast.success("Rôle modifié avec succès");
    } catch (e) {
      toast.error("Erreur lors de la modification");
    }
  };

  // [MIGRATION-MONGODB] Réinitialiser le mot de passe via API
  const handleResetPassword = async (member) => {
    const newPassword = generatePassword();
    try {
      await axios.post(`${API}/team/members/${member.id}/reset-password`, {
        new_password: newPassword
      }, { headers: apiHeaders });
      setResetPasswordMember({ ...member, newPassword });
    } catch (e) {
      toast.error("Erreur lors de la réinitialisation");
    }
  };

  // [MIGRATION-MONGODB] Toggle actif/inactif via API
  const handleToggleActive = async (member) => {
    try {
      const res = await axios.put(`${API}/team/members/${member.id}`, {
        actif: !member.actif
      }, { headers: apiHeaders });
      setTeam(prev => prev.map(m => m.id === member.id ? res.data : m));
      toast.success(member.actif ? "Compte désactivé" : "Compte réactivé");
    } catch (e) {
      toast.error("Erreur lors de la modification");
    }
  };

  // [MIGRATION-MONGODB] Supprimer un membre via API
  const handleDelete = async () => {
    if (!deletingMember) return;
    try {
      await axios.delete(`${API}/team/members/${deletingMember.id}`, { headers: apiHeaders });
      setTeam(prev => prev.filter(m => m.id !== deletingMember.id));
      setDeletingMember(null);
      toast.success("Membre supprimé");
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div
        className="rounded-[10px] p-4 px-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        style={{ backgroundColor: accentColor }}
      >
        <div>
          <h1 className="text-xl font-bold text-white">Mon équipe</h1>
          <p className="text-white/80 text-sm">
            Gérez les accès de vos collaborateurs
          </p>
        </div>
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-white text-black hover:bg-white/90 rounded-[10px] px-5"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un membre
        </Button>
      </div>

      {/* Liste des membres ou état vide */}
      {team.length === 0 ? (
        <EmptyState onAdd={() => setIsCreateModalOpen(true)} accentColor={accentColor} />
      ) : (
        <div>
          <AnimatePresence mode="popLayout">
            {team.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                accentColor={accentColor}
                onEdit={(m) => setEditingMember(m)}
                onResetPassword={handleResetPassword}
                onToggleActive={handleToggleActive}
                onDelete={(m) => setDeletingMember(m)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Modale création */}
      <CreateMemberModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleAddMember}
        accentColor={accentColor}
        existingEmails={existingEmails}
      />

      {/* Modale changement de rôle */}
      <ChangeRoleModal
        isOpen={!!editingMember}
        onClose={() => setEditingMember(null)}
        member={editingMember}
        onSave={handleChangeRole}
        accentColor={accentColor}
      />

      {/* Dialog confirmation suppression */}
      <AlertDialog open={!!deletingMember} onOpenChange={() => setDeletingMember(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce membre ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. {deletingMember?.prenom} {deletingMember?.nom} ne pourra plus accéder à la boutique.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog nouveau mot de passe */}
      <AlertDialog open={!!resetPasswordMember} onOpenChange={() => setResetPasswordMember(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Nouveau mot de passe</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Le mot de passe de {resetPasswordMember?.prenom} a été réinitialisé.
              </p>
              <div className="bg-[#0F0F0F] rounded-lg p-3 font-mono text-center text-lg text-white">
                {resetPasswordMember?.newPassword}
              </div>
              <p className="text-xs text-gray-500">
                Communiquez ce mot de passe à votre collaborateur.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction style={{ backgroundColor: accentColor }}>
              Compris
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
